<template>
	<div v-if="cart.cpu" class="starter-pc-cart">
		<loading v-if="processingCheckout">
			Processing Checkout
		</loading>
		<rule-modal
			@close="setRules([])"
		/>
		<div class="container">
			<header-subtotal
				:action="() => checkout('Top')"
				:button-class="{ 'disable-element': processingCheckout }"
			>
				{{ processingCheckout ? 'Processing...' : 'Checkout' }}
			</header-subtotal>
			<hr>
			<pc-specs
				:display-details="false"
				:parts="parts"
				:ram-capacity="ramCapacity"
				:ram-quantity="ramQuantity"
				class="pc-specs"
			>
				<template #specs-heading>
					<specs-cart-heading />
				</template>
			</pc-specs>
			<supply-shortage-note />
			<cart-features :which="which" />
			<cart-accessories-list :which="which" />
			<shipping-taxes class="hide" />
			<div class="sub-total-container">
				<sub-total
					:action="() => checkout('Bottom')"
					:button-class="{ 'disable-element': processingCheckout }"
					class="sub-total"
				>
					{{ processingCheckout ? 'Processing...' : 'Checkout' }}
				</sub-total>
			</div>
		</div>
	</div>
	<loading v-else />
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { deviceType } from "../../../../util/helpers";
import { heapAnalytics } from "../../../../util/mixins";
import HelpersMixin from "../helpers-mixin";
import CONSTANTS from "../../../../config/constants";
import HeaderSubtotal from "./header-subtotal";
import PcSpecs from "../pc-specs";
import SpecsCartHeading from "./specs-cart-heading";
import CartFeatures from "./cart-features";
import CartAccessoriesList from "./accessories-list";
import SubTotal from "../accessories-store/sub-total-row/sub-total";
import RuleModal from "../accessories-store/rule-modal";
import ShopifyClient from "shopify-buy";
import ShippingTaxes from "./shipping-taxes";
import SupplyShortageNote from "./supply-shortage-note";
import { gtmTrigger, gtmTrack } from "@/util/gtm";

export default {
	name: "StarterPcCart",
	components: {
		CartAccessoriesList,
		CartFeatures,
		HeaderSubtotal,
		PcSpecs,
		RuleModal,
		SpecsCartHeading,
		SubTotal,
		ShippingTaxes,
		SupplyShortageNote
	},
	mixins: [
		heapAnalytics,
		HelpersMixin
	],
	data() {
		return {
			CONSTANTS,
			processingCheckout: false
		};
	},
	computed: {
		...mapState({
			parts: state => state.StarterPc.builds[state.StarterPc.buildsIndex].parts,
			games: state => state.StarterPc.games,
			resolution: state => state.StarterPc.resolution,
			rules: state => state.StarterPc.rules,
			total: state => state.StarterPc.total
		}),
		...mapGetters({
			trackStarterType: "StarterPc/getTrackType"
		}),
		ramCapacity() {
			return this.getRealCapacity(this.parts.ram);
		},
		ramQuantity() {
			return Number.parseInt(this.parts.ram.quantity) * Math.abs(this.parts.ram.slots);
		}
	},
	watch: {
		rules(list) {
			if (list.length) {
				const rulesText = [];

				list.forEach(item => {
					if (!["enable", "disabled"].includes(item.ruleType)) {
						rulesText.push(item.msg);
					}
				});

				if (rulesText.length) {
					this.$modal.show("ruleModal", {
						title: "Error",
						content: rulesText.join("<br>")
					});
				}
			}
		}
	},
	created() {
		if (_.isEmpty(this.cart)) {
			this.$store.dispatch("StarterPc/getData", {
				which: this.$route.meta.group,
				route: this.which
			});
		}
	},
	beforeMount() {
		window.addEventListener("beforeunload", this.checkNavigation);
	},
	beforeDestroy() {
		window.removeEventListener("beforeunload", this.checkNavigation);
	},
	methods: {
		...mapActions({
			setRules: "StarterPc/setRules"
		}),
		checkout(pageLocation) {
			this.processingCheckout = true;
			this.trackAll(`Client: Click ${this.trackStarterType} CheckOut`, {
				page_location: pageLocation,
				page: "Cart Page"
			});
			gtmTrigger.checkoutClick(pageLocation);

			axios({
				url: `/${this.$route.meta.group}/checkout`,
				method: "POST",
				headers: {
					token: CONFIG.SESSION
				}
			}).then(async(response) => {
				let shopifyCheckout = null;
				const customAttributes = [
					{
						key: "bld_number",
						value: response.data.bld_number
					},
					{
						key: "source_device",
						value: deviceType()
					},
					{
						key: "heap_uuid",
						value: localStorage.getItem("heap-uuid")
					},
					{
						key: "checkout",
						value: this.which
					},
					{
						key: "preBuild",
						value: "1"
					},
					{
						key: "pcSKU",
						value: this.gtmPrebuildVariables.pcSKU
					},
					{
						key: "pcName",
						value: this.gtmPrebuildVariables.pcName
					},
					{
						key: "pcPrice",
						value: this.gtmPrebuildVariables.pcPrice
					},
					{
						key: "pcColor",
						value: this.gtmPrebuildVariables.pcColor || ""
					}
				];

				const shopifyClient = await ShopifyClient.buildClient({
					domain: process.env.MIX_SHOPIFY_DOMAIN,
					storefrontAccessToken: process.env.MIX_SHOPIFY_SDK_KEY
				});
				let currencyCode = {};

				if (this.$store.state.User.location.country_code == "CA") {
					currencyCode = { presentmentCurrencyCode: "CAD" };
				}

				await shopifyClient.checkout.create({ customAttributes, ...currencyCode }).then(checkout => {
					shopifyCheckout = checkout;
				});

				shopifyClient.checkout.addLineItems(
					shopifyCheckout.id,
					response.data.checkout_list
				).then(() => {
					gtmTrack.userCheckout();
					location.href = shopifyCheckout.webUrl;
				}).catch(() => {
					//
				});
			}).catch(() => {
				this.processingCheckout = false;
			});
		},
		getRealCapacity(part) {
			const suffix = part.capacity.match(/([^\d]+)/)[0].trim();
			const quantity = Number.parseInt(part.quantity) * Math.abs(part.slots);
			const realSize = Number.parseInt(part.capacity) * quantity;

			return `${realSize}${suffix}`;
		},
		checkNavigation() {
			sessionStorage.setItem("last-prebuild-in-checkout-flow", JSON.stringify({
				path: this.$route.path,
				group: this.$route.meta.group
			}));
		}
	}
};
</script>

<style lang="scss" scoped>
@import "~@s/_asset-mixers.scss";

.starter-pc-cart {
    background: $white;
    padding-bottom: 100px;

    hr {
        border-top: 1px solid #dadde0;
    }

    .sub-total-container {
        margin-top: 50px;
        display: flex;
        justify-content: flex-end;

        @media (min-width: 320px) and (max-width: 580px) {
            margin-top: 10px;
            justify-content: flex-start;
        }
    }
}
</style>
